import React from "react";
import { Icon, IconLoading } from "../../Icon";
import { colours } from "../../constants/colours";
import {
  DataDisplayWithAdornment,
  type DataDisplayWithAdornmentProps,
} from "../_components/DataDisplayWithAdornment";
import type { CalculatedFieldDataDisplayProps } from "./types";

const CalculatedFieldDataDisplay = ({
  value,
  displaySlot,
  alignment = "left",
  showHint = false,
  invalidText = "Unsupported calculated value",
  placeholder = "Enter required input values to calculate",
  busyText = "Calculating...",
  validText = "Successfully calculated",
  staticAdornmentAltText = "Calculated Field",
  hintSlot,
  className,
  style,
  isInline = false,
  isLoading = false,
  isBusy = false,
  isReady = false,
  isStatic = true,
  "aria-invalid": ariaInvalid = false,
}: CalculatedFieldDataDisplayProps) => {
  const getStatusMessage = () => {
    if (isBusy) {
      return busyText;
    }

    if (isReady) {
      return ariaInvalid ? invalidText : validText;
    }

    return placeholder;
  };

  const getStatusIcon = ({
    iconProps,
  }: {
    iconProps: Parameters<
      DataDisplayWithAdornmentProps["adornmentSlot"]
    >[0]["iconProps"];
  }) => {
    if (isBusy)
      return (
        <IconLoading
          {...iconProps}
          speed="slow"
        />
      );
    if (isReady)
      return !ariaInvalid ? (
        <Icon
          {...iconProps}
          variant="check-circle"
          colour={colours.status.success}
          alt="Success"
        />
      ) : (
        <Icon
          {...iconProps}
          variant="warning-circle"
          colour={colours.status.error}
          alt="Error"
        />
      );

    return (
      <Icon
        {...iconProps}
        variant="info"
        colour={colours.status.info}
        alt="Information"
      />
    );
  };

  const isEmpty = value === "" || value === undefined;

  return (
    <DataDisplayWithAdornment
      className={className}
      style={style}
      alignment={alignment}
      isInline={isInline}
      isLoading={isLoading}
      isBusy={isBusy}
      adornmentSlot={({ iconProps, className }) => (
        <span className={className}>
          {isStatic ? (
            <Icon
              {...iconProps}
              variant="calculator"
              alt={
                !ariaInvalid || isEmpty ? staticAdornmentAltText : invalidText
              }
              {...(ariaInvalid ? { colour: colours.status.warning } : {})}
              {...(hintSlot
                ? {
                    "aria-roledescription":
                      "Click for more context about how the field value was calculated.",
                  }
                : {})}
            />
          ) : (
            getStatusIcon({ iconProps })
          )}
        </span>
      )}
      showAdornment={showHint}
      detailsSlotDisplayMode={isStatic ? "popover" : "tooltip"}
      detailsSlot={isStatic ? hintSlot : getStatusMessage()}
      as="div"
    >
      {displaySlot({
        value,
        alignment,
        isInline,
        isBusy,
        isLoading,
        style: !isEmpty || !ariaInvalid ? { fontWeight: "bold" } : {},
      })}
    </DataDisplayWithAdornment>
  );
};

export default CalculatedFieldDataDisplay;
